import { useState, useRef, useEffect, useContext } from "react";
import { GdkToasts } from "@gdk/toasts";
import { GdkLoader } from "@gdk/loader";
import {
  BANKING_ACCOUNT_NEEDS_UPDATING,
  DOCUSIGN_DECLINE,
  DOCUSIGN_EVENT,
  DOCUSIGN_SIGNING_COMPLETE,
} from "../utils/constants/index";
import { useNavigate } from "react-router-dom";
import {
  UserContext,
  UserIncentiveSummaryContext,
  UserPendingPromotionsContext,
} from "../context/userContext";
import { FeatureFlagContext } from "../context/FeatureFlagContextProvider";
import ActivePromotions from "./CampaignCards/ActivePromotions";
import ActivePromotionsVersionTwo from "./CampaignCards/ActivePromotionsVersionTwo";
import PendingPromotions from "./CampaignCards/PendingPromotions";
import UpcomingPromotions from "./CampaignCards/UpcomingPromotions";
import { getDaysRemaining, toPascalCase } from "../helpers/incentiveHelper";
import {
  getIncentiveIdFromQuery,
  removeIncentiveIdFromUrl,
  resolveChosenIncentiveId,
} from "../helpers/incentiveDeepLinkHelper";
import ProgressBar from "./ProgressBar";
import * as rewardsApi from "../services/ApiServices/ApiService";
import { logTrace } from "../services/ApiServices/LoggingService";
import ActivePromotionModal from "./CampaignCards/ActivePromotionModal";
import UniversalModal from "./UniversalModal";
import * as promotionTypes from "../utils/constants/promotionTypes";
import * as incentiveTypes from "../utils/constants/incentiveTypes";
import SelectedPromotion from "./SelectedPromotion";
import PrizeWheel from "./PrizeWheel";
import "../styles/wheel.css";
import "../styles/Home.css";
import "../styles/gdk";
import { trackButtonClick, trackModalLoad } from "../services/gtm/gtmTracker";
import { buildWireId, getPageName, PROMOTION_DETAILS_MODAL } from "../services/gtm/pageNames";

const PROMOTION_FILTER_VALUES = Object.freeze({
  "": "none",
  Agency: "agency",
  Producer: "producer",
});

const Home = () => {
  const [agentRole, setAgentRole] = useState("");
  const [participationLevel, setParticipationLevel] = useState("");
  const gdkToast = useRef(null);
  const gdkHomeLoader = useRef(null);
  const [toastHeader, setToastHeader] = useState("");
  const [toastDescription, setToastDescription] = useState("");
  const navigate = useNavigate();
  const user = useContext(UserContext);
  const [agentFirstName, setAgentFirstName] = useState("");

  const userIncentiveSummary = useContext(UserIncentiveSummaryContext);
  const featureFlags = useContext(FeatureFlagContext);
  const userPendingPromotions = useContext(UserPendingPromotionsContext);
  const [chosenIncentiveId, setChosenIncentiveId] = useState("");
  const [incentiveType, setIncentiveType] = useState("");
  const [incentiveName, setIncentiveName] = useState("");
  const [currentIncentive, setCurrentIncentive] = useState(null);
  const [numberOfSpins, setNumberOfSpins] = useState(0);
  const [daysLeftBeforeEndDate, setDaysLeftBeforeEndDate] = useState(0);
  const [redeemDaysLeft, setRedeemDaysLeft] = useState(0);
  const [nextPayout, setNextPayout] = useState();
  const [spifProgress, setSpifProgress] = useState();
  const [spifGoal, setSpifGoal] = useState();
  const [spifIteration, setSpifIteration] = useState(0);
  const [promotionType, setPromotionType] = useState();
  const [maxNumberOfPayOuts, setMaxNumberOfPayOuts] = useState(0);
  const [principalFilter, setPrincipalFilter] = useState("");
  const [isUserActive, setIsUserActive] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [pendingPromosFlag, setPendingPromosFlag] = useState(false);
  const [gameOnHomePageFlag, setGameOnHomePageFlag] = useState(false);
  const [updatedSelectedPromotionFlag, setUpdatedSelectedPromotionFlag] = useState(false);
  const [hasPendingPromotions, setHasPendingPromotions] = useState(true);
  const [prizeWheelSoundFlag, setPrizeWheelSoundFlag] = useState(false);
  const [updatedActivePromotionsFlag, setUpdatedActivePromotionsFlag] = useState(false);
  const [updatedOneToOneSpifFlag, setUpdatedOneToOneSpifFlag] = useState(false);
  const [spinsByIncentive, setSpinsByIncentive] = useState({});
  const [liveEarnings, setLiveEarnings] = useState({});

  const handleFeatureFlags = () => {
    setPendingPromosFlag(featureFlags?.PendingPromotionsFlag);
    setGameOnHomePageFlag(featureFlags?.PrizeWheelOnHomePageFlag);
    setPrizeWheelSoundFlag(featureFlags?.PrizeWheelSoundFlag);
    setUpdatedSelectedPromotionFlag(featureFlags?.UpdatedSelectedPromotionContainerFlag);
    setUpdatedActivePromotionsFlag(featureFlags?.UpdatedActivePromotionsContainerFlag);
    setUpdatedOneToOneSpifFlag(featureFlags?.UpdatedOneToOneSpifFlag);
  };

  async function setUserFields() {
    if (user) {
      setAgentFirstName(toPascalCase(user.firstName));
      setAgentRole(user.role);
      setParticipationLevel(user.enrollmentStatus.participationLevel);
    }
  }

  async function setInitialChosenIncentive() {
    if (userIncentiveSummary && userIncentiveSummary.length > 0) {
      const incentiveIdFromQuery = getIncentiveIdFromQuery();
      const resolvedIncentiveId = resolveChosenIncentiveId(
        userIncentiveSummary,
        incentiveIdFromQuery
      );
      setChosenIncentiveId(resolvedIncentiveId);

      if (incentiveIdFromQuery !== null) {
        removeIncentiveIdFromUrl();
      }
    }
  }

  const handleSelectViewChange = (event) => {
    const selectedView = event.target.value;
    const upcomingSection = document.getElementById("upcomingSection");
    setPrincipalFilter(selectedView);
    const filterValue = PROMOTION_FILTER_VALUES[selectedView];
    if (filterValue) {
      const pageName = getPageName("/home");
      trackButtonClick({
        pageName,
        buttonId: buildWireId(pageName, "filter-promotions"),
        newValue: filterValue,
      });
    }

    if (
      selectedView !== "Producer" &&
      agentRole.toUpperCase() === "IA PRINCIPAL"
    ) {
      upcomingSection.style.display = "block";
    } else {
      upcomingSection.style.display = "none";
    }
  };

  useEffect(() => {
    initializeGdkToast();
    initializeGdkLoader();
    handleUserIncentiveSummary();
    updateGeckoPadding();
  }, [userIncentiveSummary]);

  const initializeGdkToast = () => {
    gdkToast.current = new GdkToasts({
      content: "#confirmation-toast",
      type: "update",
      autoShow: false,
      floatTime: 3000,
      closeBtn: true,
      baseTimer: true,
    });
  };

  const initializeGdkLoader = () => {
    if (!gdkHomeLoader.current) {
      gdkHomeLoader.current = new GdkLoader({
        content: "#home-loader",
        type: "section",
        autoShow: false,
      });
    } else {
      userIncentiveSummary ? gdkHomeLoader.current.hide() : gdkHomeLoader.current.show();
    }
  };

  const handleUserIncentiveSummary = () => {
    if (userIncentiveSummary) {
      setInitialChosenIncentive();
      handleFeatureFlags();
      setIsUserActive(userIncentiveSummary.length > 0);
    }
  };

  const updateGeckoPadding = () => {
    const homeGeckoElement = document.getElementById("home-gecko");
    if (homeGeckoElement) {
      const isBothPrincipal = participationLevel.toUpperCase() === "BOTH" && agentRole.toUpperCase() === "IA PRINCIPAL";
      const hasGeckoPadding = homeGeckoElement.classList.contains("gecko-padding");

      if (!isBothPrincipal && !hasGeckoPadding) {
        homeGeckoElement.classList.add("gecko-padding");
      } else if (isBothPrincipal && hasGeckoPadding) {
        homeGeckoElement.classList.remove("gecko-padding");
      }
    }
  };

  useEffect(() => {
    if (userPendingPromotions) {
      setHasPendingPromotions(userPendingPromotions.length > 0);
    }
  }, [userPendingPromotions]);

  const getIncentiveType = (incentiveId) => {
    for (const incentive of userIncentiveSummary) {
      if (incentive.incentiveId === incentiveId) {
        return {
          incentiveType: incentive.incentiveType.toUpperCase(),
          incentive: incentive,
        };
      }
    }
  };

  const getGameIncentiveDetails = (incentive) => {
    setIncentiveType(incentiveTypes.Gamification);
    setIncentiveName(incentive.incentiveName);
    setNumberOfSpins(incentive.gamificationTokens.tokensAvailable);
    setDaysLeftBeforeEndDate(getDaysRemaining(incentive.endDate));
    setRedeemDaysLeft(getDaysRemaining(incentive.redemptionEndDate));
  };

  const getSpifIncentiveDetails = (incentive) => {
    setIncentiveType(incentiveTypes.Spif);
    setIncentiveName(incentive.incentiveName);
    setNextPayout(incentive.spifProgress.nextPayoutAmount);
    setSpifProgress(incentive.spifProgress.progress);
    setSpifGoal(incentive.spifProgress.nextIterationGoal);
    setDaysLeftBeforeEndDate(getDaysRemaining(incentive.endDate));
    setRedeemDaysLeft(getDaysRemaining(incentive.redemptionEndDate));
    setPromotionType(incentive.promotionType);
    setSpifIteration(incentive.spifProgress.iteration);
    setMaxNumberOfPayOuts(incentive.maxNumberOfPayOuts);
  };

  const toggle = () => {
    setModalVisible((prev) => {
      const next = !prev;
      if (next) {
        trackModalLoad(PROMOTION_DETAILS_MODAL);
      }
      return next;
    });
  };

  useEffect(() => {
    if (userIncentiveSummary && chosenIncentiveId) {
      handleIncentiveDetails(chosenIncentiveId);
    }
  }, [chosenIncentiveId]);

  const handleIncentiveDetails = (incentiveId) => {
    const { incentiveType, incentive } = getIncentiveType(incentiveId);

    if (incentiveType === incentiveTypes.Gamification) {
      getGameIncentiveDetails(incentive);
    } else if (incentiveType === incentiveTypes.Spif) {
      getSpifIncentiveDetails(incentive);
    }

    setCurrentIncentive(incentive);
  };

  const [payoutOptions, setPayoutOptions] = useState([
    { option: "$1" },
    { option: "$2" },
  ]);

  useEffect(() => {
    for (let i = 0; i < userIncentiveSummary?.length; i++) {
      if (
        userIncentiveSummary[i].incentiveId === chosenIncentiveId &&
        userIncentiveSummary[i].incentiveType === "Gamification"
      ) {
        let wheelOptions = [];
        let payouts = userIncentiveSummary[i].gamificationPayoutOptions.map(
          (option) => option.payout
        );

        payouts.forEach((element) =>
          wheelOptions.push({ option: "$" + element.toLocaleString() })
        );

        setPayoutOptions(wheelOptions);
        break;
      }
    }
  }, [chosenIncentiveId, userIncentiveSummary]);

  const updateAgentTaxInfoOnFile = async (change) => {
    let apiCallSuccess = false;
    let postResult;

    const handleError = (error) => {
      logTrace(
        error,
        "error when calling rewardsApi.updateAgentTaxInfoOnFile",
        "updateAgentTaxInfoOnFile in Home.jsx"
      );
    };
    const handleSuccess = (responseData) => {
      apiCallSuccess = true;
      postResult = responseData;
    };

    await rewardsApi.updateAgentTaxInfoOnFile(
      change,
      { data: change },
      handleSuccess,
      handleError
    );
    if (apiCallSuccess) {
      return postResult;
    }
  };

  const uploadTaxForm = async () => {
    const handleError = (error) => {
      logTrace(
        error,
        "error when calling rewardsApi.uploadTaxInfo",
        "uploadTaxForm in Home.jsx"
      );
    };
    const handleSuccess = () => {};
    await rewardsApi.uploadTaxInfo(handleSuccess, handleError);
  };

  useEffect(() => {
    if (user) {
      handleUserInitialization();
      handleDocuSignEvent();
    }
  }, [user]);

  const handleUserInitialization = () => {
    setUserFields();
    if (user?.enrollmentStatus?.bankingStatus === BANKING_ACCOUNT_NEEDS_UPDATING) {
      navigate("/onboarding", { state: { editMode: true } });
    }
  };

  const handleDocuSignEvent = () => {
    const query = new URLSearchParams(window.location.search);
    const eventType = query.get(DOCUSIGN_EVENT);

    if (!eventType) return;

    switch (eventType) {
      case DOCUSIGN_SIGNING_COMPLETE:
        handleDocuSignSuccess();
        break;
      case DOCUSIGN_DECLINE:
        handleDocuSignDecline();
        break;
      default:
        navigate("/Error");
    }

    window.history.replaceState(
      {},
      document.title,
      (() => {
        query.delete(DOCUSIGN_EVENT);
        const queryString = query.toString();
        return (
          window.location.origin +
          window.location.pathname +
          (queryString ? `?${queryString}` : "")
        );
      })()
    );
  };

  const handleDocuSignSuccess = () => {
    setToastHeader("Success");
    setToastDescription("Your document has been signed successfully.");
    gdkToast.current.show();
    if (user && !user.enrollmentStatus.taxInfo) {
      updateAgentTaxInfoOnFile(true);
    }
    uploadTaxForm();
  };

  const handleDocuSignDecline = () => {
    if (user && !user.enrollmentStatus.taxInfo) {
      navigate("/Onboarding");
    }
  };

  const getPayoutToken = async (chosenIncentiveId) => {
    let apiCallSuccess = false;
    let payoutResult;

    const handleError = (error) => {
      logTrace(
        error,
        "error when calling rewardsApi.getPayoutToken",
        "getPayoutToken in PrizeWheel.jsx"
      );
    };

    const handleSuccess = (responseData) => {
      apiCallSuccess = true;
      if (responseData.status === "Failure" || responseData.status === 500) {
        window.location.href = "/error";
      } else {
        payoutResult = responseData.response.payoutAmount;
      }
    };

    await rewardsApi.getPayoutToken(
      chosenIncentiveId,
      handleSuccess,
      handleError
    );
    if (apiCallSuccess) {
      return payoutResult;
    }
  };

  useEffect(() => {
    if (gameOnHomePageFlag && userIncentiveSummary) {
      const spins = userIncentiveSummary.reduce((acc, incentive) => {
        if (incentive.gamificationTokens) {
          acc[incentive.incentiveId] =
            incentive.gamificationTokens.tokensAvailable;
        }
        return acc;
      }, {});
      setSpinsByIncentive(spins);
    }
  }, [userIncentiveSummary, gameOnHomePageFlag]);

  useEffect(() => {
    if (gameOnHomePageFlag && chosenIncentiveId) {
      setNumberOfSpins(spinsByIncentive[chosenIncentiveId] || 0);
    }
  }, [chosenIncentiveId, spinsByIncentive, gameOnHomePageFlag]);

  const updateSpinsForIncentive = (incentiveId, newSpins) => {
    setSpinsByIncentive((prevSpins) => ({
      ...prevSpins,
      [incentiveId]: newSpins,
    }));
  };

  useEffect(() => {
    if (userIncentiveSummary) {
      const promotionEarnings = {};
      userIncentiveSummary.forEach((i) => {
        promotionEarnings[i.incentiveId] = i.totalEarnings;
      });
      setLiveEarnings(promotionEarnings);
    }
  }, [userIncentiveSummary]);

  const updatePromotionEarnings = (incentiveId, prizeAmount) => {
    if (incentiveId && prizeAmount !== undefined) {
      setLiveEarnings((prevEarnings) => ({
        ...prevEarnings,
        [incentiveId]: (prevEarnings[incentiveId] || 0) + prizeAmount,
      }));
    }
  };

  return (
    <>
      <div id="home-loader" className="loader-container" data-testid="loader">
        <div className="loader-content">
          <div className="loader loader--animated" aria-label="Please wait">
            <div className="animated-loader-copy">
              Loading Your Rewards Page
              <div className="ellipses">
                <span className="ellipses-1">.</span>
                <span className="ellipses-2">.</span>
                <span className="ellipses-3">.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {modalVisible && (
        <UniversalModal
          setShow={setModalVisible}
          header={currentIncentive.incentiveName}
          contentType={promotionTypes.ActivePromotion}
          incentive={currentIncentive}
        />
      )}
      <div id="confirmation-toast" className="toasts-container">
        <div className="toasts toasts--confirmation">
          <div>
            <span className="icon-confirmation"></span>
          </div>
          <output>
            <div className="toasts--heading">{toastHeader}</div>
            <div className="toasts--content">{toastDescription}</div>
          </output>
          <button className="toasts--close-btn icon-close">
            <div id="toasts--base-timer"></div>
          </button>
        </div>
      </div>

      <div className="main-home-header-padding">
        <div className="main-home-header-row">
          <span
            id="main-home-header-icon"
            className="geico-icon icon-celebration icon--primary-blue"
          ></span>
          <h2 className="main-home-header">
            Welcome to your GEICO Sales Rewards, {agentFirstName}!
          </h2>
        </div>
      </div>

      <div>
        <form>
          <div className="row">
            <div className="col-4">
              {participationLevel.toUpperCase() === "BOTH" &&
                agentRole.toUpperCase() === "IA PRINCIPAL" && (
                  <div className="principal-filter-figma">
                    <label htmlFor="principal-select" className="text">
                      Filter Promotions by:
                    </label>
                    <div className="select-box principal-box">
                      <select
                        id="principal-select"
                        name="principal-select"
                        data-trackid="filter-promotions"
                        onChange={handleSelectViewChange}
                      >
                        <option value="">None</option>
                        <option value="Agency">Agency</option>
                        <option value="Producer">Producer</option>
                      </select>
                    </div>
                  </div>
                )}
              {userIncentiveSummary && (
                <>
                  {isUserActive &&
                    (updatedActivePromotionsFlag ? (
                      <ActivePromotionsVersionTwo
                        updateChosenIncentiveId={setChosenIncentiveId}
                        principalFilter={principalFilter}
                        chosenIncentiveId={chosenIncentiveId}
                        liveEarnings={liveEarnings}
                      />
                    ) : (
                      <ActivePromotions
                        updateChosenIncentiveId={setChosenIncentiveId}
                        principalFilter={principalFilter}
                        chosenIncentiveId={chosenIncentiveId}
                        liveEarnings={liveEarnings}
                      />
                    ))}
                  {pendingPromosFlag && hasPendingPromotions && <PendingPromotions />}
                </>
              )}
              {userIncentiveSummary && !isUserActive && !hasPendingPromotions && (
                <div>
                  <img id="home-gecko" src="/images/geckoPose.png" alt="Gecko" />
                </div>
              )}
            </div>
            <div className="col-8">
              {userIncentiveSummary &&
                isUserActive &&
                incentiveType === incentiveTypes.Gamification &&
                (updatedSelectedPromotionFlag ? (
                  <SelectedPromotion
                    incentiveName={incentiveName}
                    type={incentiveTypes.Gamification}
                    chosenIncentiveId={chosenIncentiveId}
                    daysLeftBeforeEndDate={daysLeftBeforeEndDate}
                    numberOfSpins={numberOfSpins}
                    payoutOptions={payoutOptions}
                    prizeWheelSoundFlag={prizeWheelSoundFlag}
                    getPayoutToken={getPayoutToken}
                    updateSpinsForIncentive={updateSpinsForIncentive}
                    updatePromotionEarnings={updatePromotionEarnings}
                    toggle={toggle}
                  />
                ) : (
                  <PrizeWheel
                    chosenIncentiveId={chosenIncentiveId}
                    incentiveName={incentiveName}
                    daysLeftBeforeEndDate={daysLeftBeforeEndDate}
                    redeemDaysLeft={redeemDaysLeft}
                    numberOfSpins={numberOfSpins}
                    payoutOptions={payoutOptions}
                    prizeWheelSoundFlag={prizeWheelSoundFlag}
                    getPayoutToken={getPayoutToken}
                    updateSpinsForIncentive={updateSpinsForIncentive}
                    updatePromotionEarnings={updatePromotionEarnings}
                    toggle={toggle}
                  />
                ))}

              {userIncentiveSummary &&
                isUserActive &&
                incentiveType === incentiveTypes.Spif &&
                (updatedSelectedPromotionFlag ? (
                  <SelectedPromotion
                    incentiveName={incentiveName}
                    type={incentiveTypes.Spif}
                    promotionType={promotionType}
                    daysLeftBeforeEndDate={daysLeftBeforeEndDate}
                    spifGoal={spifGoal}
                    spifProgress={spifProgress}
                    nextPayout={nextPayout}
                    updatedOneToOneSpifFlag={updatedOneToOneSpifFlag}
                    spifIteration={spifIteration}
                    toggle={toggle}
                    maxNumberOfPayOuts={maxNumberOfPayOuts}
                  />
                ) : (
                  <div>
                    <h4 className="game-header">{incentiveName}</h4>
                    <div>
                      <p className="description-text">
                        {promotionType === "Quotes" ? "Quote " : "Sell "}
                        policies to earn prizes. Read more information about
                        this promotion{" "}
                        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                        <a
                          href="#"
                          onClick={(event) => {
                            event.preventDefault();
                            toggle();
                          }}
                        >
                          here
                        </a>
                        {"."}
                      </p>
                    </div>
                    <div>
                      {daysLeftBeforeEndDate >= 0 && (
                        <span className="days-left-counter-bold">
                          {daysLeftBeforeEndDate} days left
                        </span>
                      )}
                      {daysLeftBeforeEndDate < 0 && (
                        <span className="days-left-counter">Promotion has ended</span>
                      )}
                    </div>
                    <ProgressBar
                      goal={spifGoal}
                      progress={spifProgress}
                      payout={nextPayout ? nextPayout.toLocaleString() : '0'}
                      promotionType={promotionType}
                    />
                  </div>
                ))}

              {userIncentiveSummary && !isUserActive && (
                <div className="alert notice-bar">
                  <div className="alert--low-importance">
                    <span className="icon-info"></span>
                    <ul className="alert-list">
                      <h4 className="notice-header">
                        There are no current active promotions
                      </h4>
                    </ul>
                  </div>
                </div>
              )}
              {agentRole?.toUpperCase() === "IA PRINCIPAL" && <UpcomingPromotions />}
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default Home;
